import {
  InputAdornment,
  TextField,
  TextFieldProps,
  useForkRef,
} from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import {
  formatMoneyInputValue,
  MoneyInputValue,
  parseMoneyInputValue,
  transformMoneyInputValue,
} from './money-input.utils';

type TMoneyInputProps = {
  value: MoneyInputValue;
  // eslint-disable-next-line no-unused-vars
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  // eslint-disable-next-line no-unused-vars
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  addonBefore?: string;
  readOnly?: boolean;
};

type MoneyInputProps = Omit<TextFieldProps, 'value' | 'onChange' | 'onBlur'> &
  TMoneyInputProps;

const DECIMAL_SEPARATOR_PATTERN = '[0-9]*[.,]?[0-9]*';

const createChangeEvent = (
  event: React.ChangeEvent<HTMLInputElement>,
  value: string
): React.ChangeEvent<HTMLInputElement> => ({
  ...event,
  target: {
    ...event.target,
    value,
  },
});

const createBlurEvent = (
  event: React.FocusEvent<HTMLInputElement>,
  value: string
): React.FocusEvent<HTMLInputElement> => ({
  ...event,
  target: {
    ...event.target,
    value,
  },
});

export const MoneyInput = React.forwardRef<HTMLInputElement, MoneyInputProps>(
  function MoneyInput(
    {
      value,
      onChange,
      onBlur,
      addonBefore = 'R$',
      readOnly,
      onFocus,
      InputProps: callerInputProps,
      inputProps: callerInputPropsAttributes,
      inputRef: callerInputRef,
      ...props
    },
    ref
  ) {
    const isFocused = useRef(false);
    const isExplicitDecimal = useRef(false);
    const handleInputRef = useForkRef(ref, callerInputRef);
    const [currentValue, setCurrentValue] = useState(() =>
      formatMoneyInputValue(value)
    );

    useEffect(() => {
      if (!isFocused.current) {
        setCurrentValue(formatMoneyInputValue(value));
      }
    }, [value]);

    const handleOnFocus = (event: React.FocusEvent<HTMLInputElement>) => {
      isFocused.current = true;
      onFocus?.(event);
    };

    const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const { data, inputType } = event.nativeEvent as InputEvent;
      const isPastedValue =
        inputType === 'insertFromPaste' || inputType === 'insertFromDrop';
      const hasExplicitSeparator = /[.,]/.test(data || '');

      if (hasExplicitSeparator || isPastedValue) {
        isExplicitDecimal.current = true;
      } else if (!/[.,]/.test(event.target.value)) {
        isExplicitDecimal.current = false;
      }

      let normalizedValue = transformMoneyInputValue(
        event.target.value,
        !isPastedValue && !isExplicitDecimal.current,
        isPastedValue
      );
      const parsedValue = parseMoneyInputValue(normalizedValue);
      const previousValue = parseMoneyInputValue(currentValue);
      const isDeleting = inputType
        ? inputType.startsWith('delete')
        : event.target.value.length < currentValue.length;

      if (isDeleting && parsedValue === 0 && previousValue === 0) {
        normalizedValue = '';
      }

      setCurrentValue(normalizedValue.replace('.', ','));
      onChange(
        createChangeEvent(
          event,
          normalizedValue && parsedValue !== null ? normalizedValue : ''
        )
      );
    };

    const handleOnBlur = (event: React.FocusEvent<HTMLInputElement>) => {
      const normalizedValue = transformMoneyInputValue(event.target.value);
      const parsedValue = parseMoneyInputValue(normalizedValue);
      const emittedValue = parsedValue === null ? '' : parsedValue.toFixed(2);

      isFocused.current = false;
      setCurrentValue(emittedValue.replace('.', ','));

      onBlur?.(createBlurEvent(event, emittedValue));
    };

    return (
      <TextField
        {...props}
        type="text"
        value={currentValue}
        onFocus={handleOnFocus}
        onChange={handleOnChange}
        onBlur={handleOnBlur}
        inputRef={handleInputRef}
        inputProps={{
          inputMode: 'decimal',
          pattern: DECIMAL_SEPARATOR_PATTERN,
          ...callerInputPropsAttributes,
          style: {
            textAlign: 'right',
            ...callerInputPropsAttributes?.style,
          },
        }}
        InputProps={{
          ...callerInputProps,
          startAdornment: (
            <>
              {callerInputProps?.startAdornment}
              <InputAdornment position="start">{addonBefore}</InputAdornment>
            </>
          ),
          readOnly: readOnly ?? callerInputProps?.readOnly,
        }}
      />
    );
  }
);
